import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { api } from '../../../utils/api';
import Pagination, { getPageSize } from '../../Pagination';
import PeakToolbar from '../../PeakToolbar';
import DlBtn from '../../DlBtn';
import Bookkeeping from '../../Bookkeeping';
import Placeholder from '../Placeholder';
import type {
  AwApproveBody, AwBankLine, AwExpenseRecord, AwExpenseRecordDetail, AwExpenseRecordPage, AwPayable, AwRecordChip, AwRecordCompare,
  AwRecordDoors, AwRecordEntryRef, AwRecordReview, AwReviewGate,
} from '../../../types';
import type { AwCtx } from '../ctx';
import { amt, baht, btn, btnPrimary, card, dmy, errBox, inp, note, num, pill, pillAmber, pillGreen, pillGrey, pillRed, sectionHead, table, td, th } from '../ui';
import { Dialog, Foot, Loader, Stat, actBtn, chip, chipOn, emptyLine, field, isoDate, lbl, r2, todayIso } from '../dialog';
import { fmtDateTime } from '../../../utils/region';
import { DocEntriesBtn } from '../docEntries';
import { RecordEvidence } from '../attachments';   // owner 2026-11-02: the record's supporting files and linked records
import NewDocBtn from '../NewDocBtn';
import RowMenu, { type RowMenuEntry } from '../RowMenu';
import { FigureLine, NoCell, NoNumber, StatusCell } from '../listCells';
import { InBooksBanner } from '../../MerchantDoc/InBooksBanner';
import { isReadOnlySeat } from './fixedAssets/shared';
import { SortChip, SortTh, useListSort } from '../listSort';   // batch 12 tail T3 (FS-11): the register's sort, now kept per viewer
import { epayApi, EPAY_ERRORS, EPAY_METHODS } from '../../../utils/api/expensePayments';
import type { EpayMethod, EpayOptionBill, EpayOptions, EpayWhtCandidates } from '../../../utils/api/expensePayments';
import { ScanSheetView } from '../sourceDoc';
import { EntryDetailDialog } from '../JournalBook';
// ⛔ A deliberate import cycle, the house's (sourceDoc.tsx ↔ docEntries.tsx): the bill inside a record is
// drawn by the Payments drawer's own sections and written through its own dialogs, and Supplier bills
// opens this window. Every cross-reference is inside a component body, so nothing is read while either
// module is still evaluating.
import {
  BillDialog, NoteDialog, PaymentDialog, ReasonDialog, PayableActions, PayableBankLines, PayableFacts, PayableNotes, PayablePayments, usePayableActs,
  loadUnmatchedDebitLines, levelGross, rowWhy, unbookWhy, whtFor, whtOf, withIncome, withRate, withWht,
} from './Purchases';
import type { PayRow } from './Purchases';

// ── Expenses › Expense records (batch 7 R3a — EXP-5 / EXP-7 / EXP-9 / EXP-14 / EXP-16) ────────────
// ONE row per expense: a client's upload together with the bill made from it, or a bill with no upload
// (GET /acct-workspace/expense-records — services/expenseRecords.ts builds the union, both statuses and
// every figure). This file lays them out and derives nothing:
//   · the two statuses arrive from the server, already independent of each other (Document: Draft /
//     Approved / Posted / Voided; Payment: Unpaid / Partially paid / Paid / — when there is no bill);
//   · Paid and Outstanding are the bill's own caches, the AP cards the same apByCurrency the Payments
//     pane and the dashboard read — one card per currency, never a total across them;
//   · the category is the account a person chose and stored, as the server names it from THIS client's
//     chart — never guessed from a code or a name here.
// Voided records are hidden by the SERVER's filter (the list convention: a paged list filtered on the
// client would show short pages and a wrong total); the Voided chip asks for them.
//
// The record opens FULL SCREEN (ExpenseRecordWindow) over the list, so closing it — X, Esc, the backdrop
// — returns to the list exactly as it was (FS-9: filters, page, sort and scroll are this pane's state and
// nothing unmounts). Its address is ?exp=<key>, so a record can be handed over as a link (deep link) and
// Purchases › Supplier bills opens the same window from its rows.
//
// The 10/13 Bookkeeping grid stays, as the "VAT line items" view (design §1 #4): the companies with no
// invoicing-portal account have no records and read their input VAT there — so for them it is the
// default view, not a fallback.

/** The record window's address: ?exp=<key> on the workspace URL. One hook for both panes that open it. */
export function useRecordParam(): { key: string; open: (key: string) => void; close: () => void } {
  const [params, setParams] = useSearchParams();
  const key = params.get('exp') || '';
  const open = useCallback((k: string) => setParams(p => { const n = new URLSearchParams(p); n.set('exp', k); return n; }, { replace: true }), [setParams]);
  const close = useCallback(() => setParams(p => { const n = new URLSearchParams(p); n.delete('exp'); return n; }, { replace: true }), [setParams]);
  return { key, open, close };
}

type TF = (key: string, opts?: Record<string, unknown>) => string;
const DOC_FILTERS = ['', 'draft', 'approved', 'posted', 'voided'] as const;
const PAY_FILTERS = ['', 'unpaid', 'partial', 'paid', 'none'] as const;
// Every value the server sends, each key written in full so a grep finds it (sourceDoc.tsx's rule: an
// unknown key would print itself). A value with no key here is shown as the server sent it.
const DOC_KEY: Record<string, string> = { draft: 'aw_er_doc_draft', approved: 'aw_er_doc_approved', posted: 'aw_er_doc_posted', voided: 'aw_er_doc_voided' };
const PAY_KEY: Record<string, string> = { unpaid: 'aw_er_pay_unpaid', partial: 'aw_er_pay_partial', paid: 'aw_er_pay_paid', none: 'aw_er_pay_none' };
const pillBlue: React.CSSProperties = { ...pill, color: '#1E40AF', background: '#EFF6FF', border: '1px solid #BFDBFE' };
const docPill = (s: string): React.CSSProperties => s === 'posted' ? pillGreen : s === 'approved' ? pillBlue : s === 'voided' ? pillRed : pillGrey;
const payPillOf = (s: string): React.CSSProperties => s === 'paid' ? pillGreen : s === 'partial' ? pillAmber : pillGrey;
const CHIP_KEY: Record<string, string> = {
  withdrawn: 'aw_er_chip_withdrawn', deleted: 'aw_er_chip_deleted', void: 'aw_er_chip_void', cancelled: 'aw_er_chip_cancelled',
  returned: 'aw_er_chip_returned', line_withdrawn: 'aw_er_chip_line_withdrawn', not_vat: 'aw_er_chip_not_vat',
  posted_line_withdrawn: 'aw_er_chip_posted_line_withdrawn', reversal_pending: 'aw_er_chip_reversal_pending',
  posted_twice: 'aw_er_chip_posted_twice', entry_drafted: 'aw_er_chip_entry_drafted', shared_line: 'aw_er_chip_shared_line',
};
const CHIP_TONE: Record<string, React.CSSProperties> = {
  withdrawn: pillRed, deleted: pillRed, void: pillRed, cancelled: pillRed, posted_line_withdrawn: pillRed, posted_twice: pillRed,
  returned: pillAmber, line_withdrawn: pillAmber, reversal_pending: pillAmber, shared_line: pillAmber,
  entry_drafted: pillBlue, not_vat: pillGrey,
};
const chipBtn = (style: React.CSSProperties): React.CSSProperties => ({ ...style, cursor: 'pointer', fontFamily: 'inherit' });
const money = (v: number | null | undefined) => (v === null || v === undefined ? '—' : amt(v));
// The OTHER record a shared-line chip names: by its EXP number; a record with none (everything entered before
// numbering began — OQ2, no backfill) by what it is AND the supplier's number, because the two records on one
// VAT line carry the SAME supplier number (all 60 CJT pairs on Daybook do) — the number alone would name the
// row's own number back to it. Keyed by the anchor kind in the record key, each key written in full.
const REF_KIND_KEY: Record<string, string> = { u: 'aw_er_ref_scan', p: 'aw_er_ref_bill' };
const refName = (r: { key: string; no: string; supplierNo: string }, t: TF) => {
  if (r.no) return r.no;
  const kindKey = REF_KIND_KEY[r.key.slice(0, Math.max(0, r.key.indexOf(':')))];
  if (r.supplierNo) return kindKey ? t(kindKey, { no: r.supplierNo }) : r.supplierNo;
  return t('aw_er_record_unnumbered');
};

/**
 * A record's chips: what the server said about its status, in words. An entry chip opens that journal
 * entry (the voucher window, mounted over whatever is open); a shared-line chip opens the OTHER record
 * that holds the same VAT line (session decision (a): the two are listed, named, and never linked here).
 */
function RecordChips({ r, onEntry, onRecord }: { r: AwExpenseRecord; onEntry: (e: AwRecordEntryRef) => void; onRecord: (key: string) => void }) {
  const { t } = useTranslation();
  const stop = (e: React.MouseEvent) => e.stopPropagation();
  const chips: React.ReactNode[] = [];
  for (const e of r.entries.posted) {
    chips.push(
      <button key={`p-${e.id}`} type="button" onClick={ev => { stop(ev); onEntry(e); }} title={t('aw_er_chip_entry_title')} style={chipBtn(pillGreen)}>
        <i className="ti ti-book-2" aria-hidden /> {t('aw_er_chip_posted_as', { no: e.docNo || '—' })}
      </button>,
    );
  }
  r.chips.forEach((c: AwRecordChip, i: number) => {
    const style = CHIP_TONE[c.code] || pillGrey;
    const label = CHIP_KEY[c.code] ? t(CHIP_KEY[c.code], {
      no: c.entry?.docNo || '—',
      refs: (c.refs || []).map(x => refName(x, t)).join(', '),
    }) : c.code;
    if (c.entry) {
      const ent = c.entry;
      chips.push(
        <button key={`c-${i}`} type="button" onClick={ev => { stop(ev); onEntry(ent); }} title={t('aw_er_chip_entry_title')} style={chipBtn(style)}>{label}</button>,
      );
    } else if (c.code === 'shared_line' && c.refs?.length) {
      chips.push(
        <span key={`c-${i}`} style={{ display: 'inline-flex', gap: 4, flexWrap: 'wrap', alignItems: 'center' }} title={t('aw_er_chip_shared_line_title')}>
          {c.refs.map(x => (
            <button key={x.key} type="button" onClick={ev => { stop(ev); onRecord(x.key); }} style={chipBtn(style)}>
              <i className="ti ti-copy" aria-hidden /> {t('aw_er_chip_shared_line', { refs: refName(x, t) })}
            </button>
          ))}
        </span>,
      );
    } else {
      chips.push(<span key={`c-${i}`} style={style} title={c.reason || undefined}>{label}{c.reason ? ` · ${c.reason}` : ''}</span>);
    }
  });
  if (!chips.length) return null;
  return <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginTop: 4 }}>{chips}</div>;
}

/** The two status pills, side by side, each read from the server. */
function StatusPills({ r }: { r: AwExpenseRecord }) {
  const { t } = useTranslation();
  return (
    <span style={{ display: 'inline-flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
      <span style={docPill(r.doc_status)} data-doc-status={r.doc_status}>{DOC_KEY[r.doc_status] ? t(DOC_KEY[r.doc_status]) : r.doc_status}</span>
      {r.pay_status === 'none'
        ? <span style={pillGrey} title={t('aw_er_pay_none_title')} data-pay-status="none">—</span>
        : <span style={payPillOf(r.pay_status)} data-pay-status={r.pay_status}>{PAY_KEY[r.pay_status] ? t(PAY_KEY[r.pay_status]) : r.pay_status}</span>}
    </span>
  );
}

const Fact = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div style={{ minWidth: 140, flex: '0 1 auto' }}>
    <div style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: '.04em', textTransform: 'uppercase', color: 'var(--text-tertiary)' }}>{label}</div>
    <div style={{ fontSize: 13, color: 'var(--text-primary)', marginTop: 2 }}>{children}</div>
  </div>
);
const COMPARE_KEY: Record<string, string> = {
  supplier_no: 'aw_er_f_supplier_no', date: 'aw_er_f_date', supplier: 'aw_er_f_supplier',
  supplier_tax_id: 'aw_er_f_supplier_tax_id', total: 'aw_er_f_total', vat: 'aw_er_f_vat',
};
const showCell = (c: AwRecordCompare, v: string | number | null) =>
  v === null || v === '' ? '—' : (c.field === 'total' || c.field === 'vat') ? amt(v) : c.field === 'date' ? dmy(String(v)) : String(v);
const SOURCE_KEY: Record<string, string> = { bill: 'aw_er_source_bill', line: 'aw_er_source_line', ocr: 'aw_er_source_ocr' };
const SCAN_KEY: Record<string, string> = { pending: 'aw_exp_pending', approved: 'aw_exp_approved', rejected: 'aw_exp_rejected' };

/** A refusal from the server in this window's words: keyed on the code the route and the preview share,
 *  with the server's own sentence for anything this file has no words for. */
const period = (y: number, m: number) => `${String(m).padStart(2, '0')}/${y}`;
function whyText(g: AwReviewGate, t: TF): string {
  const r = g.refusal;
  if (!r) return '';
  const p = r.params || {};
  switch (r.code) {
    case 'tax_module': return t('aw_er_why_tax_module');
    case 'not_vat': return t('aw_er_why_not_vat');
    case 'outside_thailand': return t('aw_er_why_outside');
    case 'bill_booked': return t('aw_er_why_bill_booked', { bill: p.bill || '—' });
    case 'live_bill': return t('aw_er_why_live_bill', { bill: p.bill || '—' });
    case 'payments': return t('aw_er_why_payments', { bill: p.bill || '—' });
    // (Batch 11 W4 P9, LB-41) Posted in a closed period: it stays in the books — never "reverse it in Journal".
    case 'posted': return p.closed?.start
      ? t('aw_er_why_posted_closed', { no: p.entry || '—', from: dmy(String(p.closed.start)), to: dmy(String(p.closed.end)) })
      : t('aw_er_why_posted', { no: p.entry || '—' });
    case 'asset': return t('aw_er_why_asset', { asset: p.asset || '—' });
    case 'filed': return p.year ? t('aw_er_why_filed_approve', { period: period(Number(p.year), Number(p.month)), n: p.filingNo }) : t('aw_er_why_filed_line');
    case 'notes_booked': return t('aw_er_why_notes_booked');   // batch 11 R8b: a booked supplier note holds the bill's line
    // Batch 11 W3 R6 (W3-15): a payment of the bill this record's line confirms is settled in the books — its Void / Return
    // to draft wait for that settlement's reversal (the bill's payment history opens it).
    case 'settlement_posted': {
      // (W3 ship review, S4) A settlement posted in a period closed since is never reversed: its own sentence, never
      // "reverse that settlement first".
      const shut = (r.entries || []).find(e => !!e.closed && !e.reversalDraft);
      if (shut) return t('aw_er_why_settlement_posted_closed', { no: shut.docNo || '—', from: dmy(shut.closed!.start), to: dmy(shut.closed!.end) });
      // (W3.1, the state lens' low) Its reversal raised already: "post R… first", never "reverse it" (the door refuses that).
      const lead = (r.entries || []).find(e => !!p.entry && e.docNo === p.entry) || (r.entries || [])[0];
      return lead?.reversalDraft
        ? t('aw_er_why_settlement_posted_rev', { no: lead.docNo || p.entry || '—', rv: lead.reversalDraft })
        : t('aw_er_why_settlement_posted', { no: p.entry || (r.entries || [])[0]?.docNo || '—' });
    }
    default: return r.message;
  }
}
/** The route's error as a sentence (the Purchases panes' rule: a 404 says not found, else the server's words) —
 *  except a refusal that arrives at click time with a code this window already words (the scan's doors answer
 *  { error, code, params }, the same codes the preview's `review` carries): that one is said in the reader's
 *  language, exactly as the preview would have said it. */
const WHY_CODES = new Set(['tax_module', 'not_vat', 'outside_thailand', 'bill_booked', 'live_bill', 'payments', 'posted', 'filed', 'asset', 'notes_booked', 'settlement_posted']);
const errText = (e: any, t: TF): string => {
  if (e?.status === 404) return t('aw_pay_err_not_found');
  const code = String(e?.payload?.code || '');
  if (WHY_CODES.has(code)) return whyText({ offered: true, refusal: { code, message: String(e?.message || ''), params: e?.payload?.params || {}, entries: Array.isArray(e?.payload?.entries) ? e.payload.entries : undefined } }, t);
  return String(e?.message || '') || t('aw_bank_err_generic');
};
/** The same, re-thrown for the shared ReasonDialog, which shows `message` — without the status, so its 409
 *  prefix (written for a BILL: "the bill is not in the state…") does not stand in front of a scan's reason. */
const localErr = (e: any, t: TF): any => {
  if (!e || !WHY_CODES.has(String(e?.payload?.code || ''))) return e;
  const x: any = new Error(errText(e, t)); x.payload = e.payload;
  return x;
};
const Field2 = ({ label, children, span }: { label: string; children: React.ReactNode; span?: number }) => (
  <div style={span ? { gridColumn: `span ${span}` } : undefined}><label style={lbl}>{label}</label>{children}</div>
);
const fin2 = (v: string): number | null => { if (String(v).trim() === '') return null; const n = Number(v); return Number.isFinite(n) ? n : NaN; };
/** A refusal of the payment register v2 (batch 11 R5), in the reader's words by its code; anything else as errText says it. */
const epayText = (e: any, t: TF): string => {
  const code = String(e?.payload?.code || '');
  return (EPAY_ERRORS as readonly string[]).includes(code) ? t(`aw_epay_err_${code}`) : errText(e, t);
};

/**
 * ── Approve a client's scan into the VAT return (batch 7 R3b, EXP-9) ────────────────────────────────
 * Full screen: the scan's figures as OCR read them, for the reviewer to correct, and every item of the scan
 * with an account to choose from THIS client's own chart (the list GET /expense-accounts-options answers —
 * the server refuses anything else). Before the click it says which ภ.พ.30 the line goes into. The server
 * (services/uploadReview.ts approveUpload — the same function the Tax module's queue calls) writes ONE line
 * through upsertLine, then the items through attachItems.
 */
export function ApproveDialog({ ctx, recordKey, recordNo, review, ask, onClose, onDone }: { ctx: AwCtx; recordKey: string; recordNo: string; review: AwRecordReview;
  /** Why this door is shut now, or '': asked at the click, by a host whose verdict may be minutes old (a list's Status dropdown). */
  ask?: () => Promise<string>;
  onClose: () => void; onDone: () => void;
}) {
  const { t } = useTranslation();
  const f0 = review.approve.figures;
  const items = review.approve.items;
  const tgt = review.approve.target;
  const [docNo, setDocNo] = useState(f0.doc_no);
  const [lineDate, setLineDate] = useState(f0.line_date);
  const [party, setParty] = useState(f0.party_name);
  const [taxId, setTaxId] = useState(f0.party_tax_id);
  const [branch, setBranch] = useState(f0.branch_no || '00000');
  const [amount, setAmount] = useState(String(f0.amount));
  const [amount0, setAmount0] = useState(String(f0.amount0));
  const [vat, setVat] = useState(String(f0.vat));
  const [total, setTotal] = useState(String(f0.total));
  const [hasVat, setHasVat] = useState(!!f0.has_vat);
  const [codes, setCodes] = useState<string[]>(() => items.map(() => ''));
  const [accounts, setAccounts] = useState<{ code: string; name_th: string; name_en: string }[]>([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  // FS-12: the opening values are the scan's OCR, given — not typed. Only the edit path marks the form.
  const [dirty, setDirty] = useState(false);
  const ed = <T,>(set: (v: T) => void, v: T) => { setDirty(true); set(v); };
  useEffect(() => {
    let alive = true;
    api.awExpenseAccountOptions(ctx.clientId).then((d: any) => { if (alive) setAccounts(d?.accounts || []); }).catch(() => { /* no picker, no harm */ });
    return () => { alive = false; };
  }, [ctx.clientId]);
  const nums = [amount, amount0, vat, total].map(fin2);
  const ready = isoDate(lineDate) && nums.every(n => n !== null && !Number.isNaN(n)) && !!party.trim() && !busy;
  const submit = async () => {
    if (!ready) return;
    setBusy(true); setErr('');
    const body: AwApproveBody = {
      lineDate, docNo: docNo.trim(), partyName: party.trim(), partyTaxId: taxId.trim(), branchNo: branch.trim() || '00000',
      amount: r2(Number(amount)), amount0: r2(Number(amount0)), vat: hasVat ? r2(Number(vat)) : 0, total: r2(Number(total)), hasVat, accountCodes: codes,
    };
    try {
      const why = ask ? await ask() : '';
      if (why) { setErr(why); setBusy(false); return; }
      await api.awExpenseRecordApprove(ctx.clientId, recordKey, body); onDone();
    }
    catch (e: any) { setErr(errText(e, t)); setBusy(false); }
  };
  const filing = tgt.filingNo > 0 ? t('aw_er_filing_additional', { n: tgt.filingNo }) : t('aw_er_filing_normal');
  return (
    // Headed with the RECORD's number (its EXP no.), never the supplier's number the reviewer may be retyping below.
    <Dialog fullscreen title={t('aw_er_approve_title')} docNo={recordNo || undefined} onClose={onClose} busy={busy} dirty={dirty}
      footer={<Foot onClose={onClose} onSubmit={submit} busy={busy} ready={ready} icon="ti ti-circle-check" label={t('aw_er_approve_btn')} t={t} />}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div style={{ ...card, padding: '9px 12px', fontSize: 13, display: 'flex', gap: 8, alignItems: 'baseline' }} data-approve-target="1">
          <i className="ti ti-receipt-tax" style={{ color: 'var(--accent)' }} aria-hidden />
          <span>{t('aw_er_approve_target', { period: period(tgt.year, tgt.month), filing })}</span>
        </div>
        <div style={note}>{t('aw_er_approve_hint')}</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
          <Field2 label={t('aw_er_f_supplier_no')}><input value={docNo} onChange={e => ed(setDocNo, e.target.value)} maxLength={120} style={field} /></Field2>
          <Field2 label={t('aw_er_f_date')}><input type="date" value={lineDate} onChange={e => ed(setLineDate, e.target.value)} style={field} /></Field2>
          <Field2 label={t('aw_er_f_branch')}><input value={branch} onChange={e => ed(setBranch, e.target.value)} maxLength={10} style={field} /></Field2>
          <Field2 label={t('aw_er_f_supplier')} span={2}><input value={party} onChange={e => ed(setParty, e.target.value)} maxLength={300} style={field} /></Field2>
          <Field2 label={t('aw_er_f_supplier_tax_id')}><input value={taxId} onChange={e => ed(setTaxId, e.target.value)} maxLength={20} style={field} /></Field2>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 10 }}>
          <Field2 label={t('aw_er_f_base')}><input type="number" step="0.01" value={amount} onChange={e => ed(setAmount, e.target.value)} style={field} /></Field2>
          <Field2 label={t('aw_er_f_base0')}><input type="number" step="0.01" value={amount0} onChange={e => ed(setAmount0, e.target.value)} style={field} /></Field2>
          <Field2 label={t('aw_er_f_vat')}><input type="number" step="0.01" value={hasVat ? vat : '0'} disabled={!hasVat} onChange={e => ed(setVat, e.target.value)} style={field} /></Field2>
          <Field2 label={t('aw_er_f_total')}><input type="number" step="0.01" value={total} onChange={e => ed(setTotal, e.target.value)} style={field} /></Field2>
        </div>
        <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 12.5 }}>
          <input type="checkbox" checked={hasVat} onChange={e => ed(setHasVat, e.target.checked)} /> {t('aw_er_approve_has_vat')}
        </label>
        <div>
          <div style={sectionHead}>{t('aw_er_items_title')}</div>
          {!items.length ? <div style={{ ...card, ...note }}>{t('aw_er_items_none')}</div> : (
            <div style={{ ...card, padding: 0, overflowX: 'auto' }}>
              <table style={table}>
                <thead><tr>
                  <th style={th}>{t('aw_er_item_desc')}</th><th style={{ ...th, textAlign: 'right' }}>{t('aw_er_item_qty')}</th>
                  <th style={{ ...th, textAlign: 'right' }}>{t('aw_er_item_price')}</th><th style={{ ...th, textAlign: 'right' }}>{t('aw_er_item_amount')}</th>
                  <th style={th}>{t('aw_er_item_code')}</th><th style={th}>{t('aw_er_item_account')}</th>
                </tr></thead>
                <tbody>
                  {items.map((it, i) => (
                    <tr key={i} data-item={i}>
                      <td style={td}>{it.description || '—'}</td>
                      <td style={num}>{it.qty || '—'}</td>
                      <td style={num}>{it.unitPrice ? amt(it.unitPrice) : '—'}</td>
                      <td style={num}>{amt(it.amount)}</td>
                      <td style={td}>{it.docItemCode || '—'}</td>
                      <td style={td}>
                        <select value={codes[i] || ''} aria-label={t('aw_er_item_account')} style={{ ...field, minWidth: 200 }}
                          onChange={e => ed(setCodes, (cs: string[]) => cs.map((c, j) => (j === i ? e.target.value : c)))}>
                          <option value="">{t('aw_er_item_account_none')}</option>
                          {accounts.map(a2 => <option key={a2.code} value={a2.code}>{a2.code} · {a2.name_th || a2.name_en}</option>)}
                        </select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {!!items.length && <div style={{ ...note, marginTop: 4 }}>{t('aw_er_items_hint')}</div>}
        </div>
        {err && <div style={errBox}>{err}</div>}
      </div>
    </Dialog>
  );
}

/**
 * ── Pay a scan that has no bill yet (batch 7 R3b) ───────────────────────────────────────────────────
 * One window, two halves (full screen since batch 12 tail T2, its Pay pinned in the footer, headed with the record's
 * number): the bill made from the upload (pre-filled from /payables/from-upload — the
 * scan's own OCR, which the person confirms, WHT included) and the payment. The server records both in
 * one transaction (POST /expense-records/:key/pay: createPayable, then recordPayment), so a payment it
 * refuses leaves no bill behind.
 */
export function PayUploadDialog({ ctx, recordKey, recordNo, uploadId, onClose, onDone }: { ctx: AwCtx; recordKey: string; recordNo?: string; uploadId: string; onClose: () => void; onDone: () => void }) {
  const { t } = useTranslation();
  const [vendor, setVendor] = useState('');
  const [taxId, setTaxId] = useState('');
  const [billNo, setBillNo] = useState('');
  const [billDate, setBillDate] = useState(todayIso());
  const [total, setTotal] = useState('');
  const [vat, setVat] = useState('');
  const [wht, setWht] = useState('');
  const [paidDate, setPaidDate] = useState(todayIso());
  const [amount, setAmount] = useState<string | null>(null);   // null = follows "to pay" until typed
  const [method, setMethod] = useState<EpayMethod>('transfer');
  const [reference, setReference] = useState('');
  const [lineId, setLineId] = useState('');
  const [lines, setLines] = useState<AwBankLine[] | null>(null);
  // Batch 11 R5: the payment names the bank account it came out of (W2-16) — the line's, else the one chosen here, else
  // the company's only one — and its date must not be in a closed period. Both asked first (GET …/expense-payments/options).
  const [accountId, setAccountId] = useState('');
  const [opts, setOpts] = useState<EpayOptions | null>(null);
  // (Batch 11 W4 R7, P7b task 3) Cash: the petty cash box it came out of — then no bank account and no statement line.
  const [boxId, setBoxId] = useState('');
  // (Batch 11 W4 P7b, LB-46) The payment's WHT — the per-bill block of the payment dialog, for the one bill this window
  // makes: the amount (the bill's printed WHT for its share), the payee type, the income type, rate and gross, or a hand
  // line of the month claimed. Sent as the old door reads it (payment.wht_*, routes/acctWorkspace.ts readOldWht).
  const [wrow, setWrow] = useState<PayRow>({ cash: '', wht: '', whtTouched: false, incomeType: '', payeeType: '', rate: '', gross: '', grossTouched: false,
    mode: '', claimLineId: '', another: false });
  const [cands, setCands] = useState<EpayWhtCandidates | null>(null);
  const [filling, setFilling] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  // FS-12 — the pre-fill is the scan's, given; the payment opens on today and "to pay". Nothing typed, nothing asked.
  const [dirty, setDirty] = useState(false);
  const ed = <T,>(set: (v: T) => void, v: T) => { setDirty(true); set(v); };
  useEffect(() => {
    let alive = true;
    api.awPayableFromUpload(ctx.clientId, uploadId).then((d: any) => {
      if (!alive) return;
      setVendor(d.vendor_name || ''); setTaxId(d.vendor_tax_id || ''); setBillNo(d.bill_no || '');
      if (d.bill_date) setBillDate(d.bill_date);
      setTotal(d.total_amount == null ? '' : String(d.total_amount)); setVat(d.vat_amount == null ? '' : String(d.vat_amount)); setWht(d.wht_amount == null ? '' : String(d.wht_amount));
    }).catch((e: any) => { if (alive) setErr(errText(e, t)); }).finally(() => { if (alive) setFilling(false); });
    loadUnmatchedDebitLines(ctx.clientId).then(rows => { if (alive) setLines(rows); }).catch(() => { if (alive) setLines([]); });
    return () => { alive = false; };
  }, [ctx.clientId, uploadId]);   // eslint-disable-line react-hooks/exhaustive-deps
  const payKey = isoDate(paidDate) ? paidDate : '';
  useEffect(() => {
    if (!payKey) return;
    let alive = true;
    epayApi.options(ctx.clientId, [], payKey).then(o => {
      if (!alive) return;
      setOpts(o); setAccountId(cur => cur || (o.bankAccounts.length === 1 ? o.bankAccounts[0].id : ''));
    }).catch(() => undefined);
    return () => { alive = false; };
  }, [ctx.clientId, payKey]);
  const nTotal = fin2(total), nVat = fin2(vat), nWht = fin2(wht);
  const numsOk = nTotal !== null && !Number.isNaN(nTotal) && nTotal >= 0 && (nVat === null || (!Number.isNaN(nVat) && nVat >= 0))
    && (nWht === null || (!Number.isNaN(nWht) && nWht >= 0 && nWht <= nTotal));
  const toPay = numsOk ? r2((nTotal as number) - (nWht || 0)) : null;
  const amt$ = amount === null ? (toPay === null ? '' : String(toPay)) : amount;
  const nAmt = Number(amt$);
  const cash = method === 'cash';
  // The bill this window makes, as the payment dialog's per-bill rules read one (rowWhy / whtFor): what it prints and owes.
  const guess = (/^\d{13}$/.test(taxId.replace(/\D/g, '')) ? (taxId.replace(/\D/g, '').startsWith('0') ? 'company' : 'individual') : '') as '' | 'company' | 'individual';
  const bill: EpayOptionBill = { id: 'new', billNo: billNo.trim(), vendor: vendor.trim(), vendorTaxId: taxId.trim(), currency: '', toPay: toPay || 0,
    outstanding: toPay || 0, whtFace: numsOk ? (nWht || 0) : 0, whtTaken: 0, whtEarlier: 0, whtDefault: numsOk ? (nWht || 0) : 0, payeeTypeGuess: guess, refusal: null };
  const canWht = !!opts?.canWht;
  const withholds = bill.whtFace > 0;
  // The WHT follows the payment's share of the bill until a person types one (the payment dialog's rule).
  useEffect(() => {
    if (wrow.whtTouched) return;
    const w = withholds && Number.isFinite(nAmt) && nAmt > 0 ? whtFor(bill, nAmt) : 0;
    setWrow(r => { const g = !r.grossTouched ? levelGross(w, Number(r.rate)) : null; return { ...r, wht: String(w), ...(g !== null ? { gross: String(g) } : {}) }; });
  }, [bill.whtFace, bill.toPay, nAmt, wrow.whtTouched]);   // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { setWrow(r => (r.payeeType ? r : { ...r, payeeType: guess })); }, [guess]);
  const wNow = Number(wrow.wht) || 0;
  const pt = wrow.payeeType || guess || 'company';
  // The month's hand WHT lines of this payee (asked before the bill exists — by the payee the form names): claim one, or
  // say this is another payment; a filed month takes no new line. Asked only while the payment withholds.
  useEffect(() => {
    if (!canWht || !withholds || !(wNow > 0) || !payKey || !(vendor.trim() || taxId.trim())) { setCands(null); return; }
    let alive = true;
    const h = setTimeout(() => {
      epayApi.whtCandidatesFor(ctx.clientId, { vendorName: vendor.trim(), vendorTaxId: taxId.trim() }, payKey, pt).then(c => { if (alive) setCands(c); }).catch(() => { if (alive) setCands(null); });
    }, 250);
    return () => { alive = false; clearTimeout(h); };
  }, [ctx.clientId, canWht, withholds, wNow > 0, payKey, vendor, taxId, pt]);   // eslint-disable-line react-hooks/exhaustive-deps
  // ONE funnel for the bill's line of this payment (the payment dialog's setLine): every edit of it marks the form.
  const setLine = (patch: Partial<PayRow>) => { setDirty(true); setWrow(r => ({ ...r, ...patch })); };
  const whtWhy = withholds ? rowWhy(bill, { ...wrow, cash: String(nAmt) }, cands || undefined, canWht, t) : '';
  const claimable = (cands?.rows || []).filter(x => x.claimable);
  // Cash: the client's active boxes opened by the pay date (the server's own before_opening rule); a later one is greyed
  // with its date; none → Cash disabled with the sentence; a statement line chosen → Cash not offered (a cash payment has none).
  const boxes = opts?.boxes || [];
  const boxOpen = (b: { openingDate: string }) => !!payKey && b.openingDate <= payKey;
  const cashBlocked = !!lineId || !boxes.length || !!opts?.cashRefusal;
  const box = boxes.find(b => b.id === boxId && boxOpen(b)) || null;
  useEffect(() => {
    if (!cash) return;
    const usable = boxes.filter(boxOpen);
    setBoxId(cur => (cur && usable.some(b => b.id === cur)) ? cur : (usable.length === 1 ? usable[0].id : ''));
  }, [cash, opts, payKey]);   // eslint-disable-line react-hooks/exhaustive-deps
  const pickLine = (id: string) => {
    setDirty(true);
    setLineId(id);
    const l = (lines || []).find(x => x.id === id);
    // A statement line is paid WHOLE (batch 11 R5, design D29): its date, its account and its amount are the payment's.
    if (l) { setPaidDate(l.txn_date); setAccountId(l.bank_account_id); setAmount(String(r2(Number(l.debit)))); }
  };
  useEffect(() => { if (cash && lineId) setLineId(''); }, [cash]);   // eslint-disable-line react-hooks/exhaustive-deps
  const pickedLine = (lines || []).find(x => x.id === lineId) || null;
  const lineOk = !pickedLine || (r2(Number(pickedLine.debit)) === r2(nAmt) && pickedLine.bank_account_id === accountId);
  const ready = !filling && !!vendor.trim() && !!billNo.trim() && isoDate(billDate) && numsOk && isoDate(paidDate)
    && Number.isFinite(nAmt) && nAmt > 0 && toPay !== null && r2(nAmt) <= toPay
    && !!opts && !opts.payDateRefusal && (cash ? !cashBlocked && !!box : !!accountId && opts.bankAccounts.some(a => a.id === accountId) && lineOk)
    && !whtWhy;
  const submit = async () => {
    if (!ready || busy) return;
    setBusy(true); setErr('');
    try {
      await api.awExpenseRecordPay(ctx.clientId, recordKey, {
        bill: { vendor_name: vendor.trim(), vendor_tax_id: taxId.trim(), bill_no: billNo.trim(), bill_date: billDate, total_amount: r2(nTotal as number), vat_amount: r2(nVat || 0), wht_amount: r2(nWht || 0) },
        payment: {
          paid_date: paidDate, amount: r2(nAmt), method, reference: reference.trim() || undefined,
          ...(cash ? { cash_box_id: boxId } : { bank_line_id: lineId || undefined, bank_account_id: accountId || undefined }),
          // (LB-46) The payment's WHT, as the old door reads it — none when the bill prints none or this share takes none.
          ...(withholds && wNow > 0 ? (wrow.mode === 'claim'
            ? { wht_amount: r2(wNow), wht_mode: 'claim' as const, wht_claim_line_id: wrow.claimLineId }
            : { wht_amount: r2(wNow), wht_mode: 'new' as const, wht_income_type: wrow.incomeType, wht_payee_type: (wrow.payeeType || undefined) as any,
                wht_rate: Number(wrow.rate), wht_gross: r2(Number(wrow.gross)), wht_another: wrow.another }) : {}),
        },
      });
      onDone();
    } catch (e: any) { setErr(epayText(e, t)); setBusy(false); }
  };
  return (
    <Dialog fullscreen title={t('aw_er_pay_title')} docNo={recordNo || undefined} onClose={onClose} busy={busy} dirty={dirty}
      footer={<Foot onClose={onClose} onSubmit={submit} busy={busy} ready={ready} icon="ti ti-cash" label={t('aw_er_pay_btn')} t={t} />}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, maxWidth: 860 }}>
        <div style={note}>{filling ? <><i className="ti ti-loader" aria-hidden /> {t('aw_pay_filling')}</> : t('aw_er_pay_hint')}</div>
        <div style={{ fontSize: 11, fontWeight: 800, textTransform: 'uppercase', color: 'var(--text-tertiary)' }}>{t('aw_er_pay_bill')}</div>
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 8 }}>
          <Field2 label={t('aw_pay_vendor')}><input value={vendor} onChange={e => ed(setVendor, e.target.value)} maxLength={200} style={field} /></Field2>
          <Field2 label={t('aw_pay_vendor_tax_id')}><input value={taxId} onChange={e => ed(setTaxId, e.target.value)} maxLength={200} style={field} /></Field2>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
          <Field2 label={t('aw_pay_bill_no')}><input value={billNo} onChange={e => ed(setBillNo, e.target.value)} maxLength={200} style={field} /></Field2>
          <Field2 label={t('aw_pay_bill_date')}><input type="date" value={billDate} onChange={e => ed(setBillDate, e.target.value)} style={field} /></Field2>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 8 }}>
          <Field2 label={t('aw_pay_total')}><input type="number" step="0.01" min="0" value={total} onChange={e => ed(setTotal, e.target.value)} style={field} /></Field2>
          <Field2 label={t('aw_pay_vat')}><input type="number" step="0.01" min="0" value={vat} onChange={e => ed(setVat, e.target.value)} style={field} /></Field2>
          <Field2 label={t('aw_pay_wht')}><input type="number" step="0.01" min="0" value={wht} onChange={e => ed(setWht, e.target.value)} style={field} data-er-bill-wht /></Field2>
          <Field2 label={t('aw_pay_to_pay')}><div style={{ ...field, background: 'transparent', border: 'none', fontWeight: 800 }}>{toPay === null ? '—' : amt(toPay)}</div></Field2>
        </div>
        <div style={{ fontSize: 11, fontWeight: 800, textTransform: 'uppercase', color: 'var(--text-tertiary)', marginTop: 4 }}>{t('aw_er_pay_payment')}</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
          <Field2 label={t('aw_pay_paid_date')}><input type="date" value={paidDate} onChange={e => ed(setPaidDate, e.target.value)} style={field} data-er-pay-date /></Field2>
          <Field2 label={t('aw_bank_amount')}>
            <input type="number" step="0.01" min="0" value={amt$} onChange={e => ed(setAmount, e.target.value)} style={field} />
            {toPay !== null && Number.isFinite(nAmt) && r2(nAmt) > toPay && <div style={{ fontSize: 11.5, color: '#B42318', marginTop: 3 }}>{t('aw_pay_amount_over', { max: amt(toPay) })}</div>}
          </Field2>
          <Field2 label={t('aw_pay_method')}>
            <select value={method} onChange={e => ed(setMethod, e.target.value as EpayMethod)} style={field} data-epay-method>
              {/* (Batch 11 W4 R7) Cash: out of a petty cash box. Not offered once a statement line is chosen (a cash payment has
                  none); disabled, with the server's sentence below, while the client has no active box. */}
              {EPAY_METHODS.filter(m2 => m2 !== 'cash' || !lineId).map(m2 => (
                <option key={m2} value={m2} disabled={m2 === 'cash' && cashBlocked} data-epay-method-option={m2}>{t(`aw_pay_method_${m2}`)}</option>
              ))}
            </select>
            {!lineId && !!opts && cashBlocked && <div style={note} data-epay-cash-refusal>{t('aw_epay_cash_refusal')}</div>}
          </Field2>
        </div>
        {cash ? (
          <Field2 label={t('aw_epay_box')}>
            <select value={boxId} onChange={e => ed(setBoxId, e.target.value)} style={field} data-epay-box>
              <option value="">{t('aw_epay_box_pick')}</option>
              {boxes.map(b => (
                <option key={b.id} value={b.id} disabled={!boxOpen(b)} data-epay-box-option={b.id} data-epay-box-closed={boxOpen(b) ? undefined : 1}>
                  {boxOpen(b) ? t('aw_epay_box_option', { name: b.name, balance: `${b.balanceSat < 0 ? '−' : ''}${baht(Math.abs(b.balanceSat))}` })
                    : t('aw_epay_box_opens', { name: b.name, date: dmy(b.openingDate) })}
                </option>
              ))}
            </select>
            {!!box && Number.isFinite(nAmt) && (() => {
              const after = box.balanceSat - Math.round(nAmt * 100);
              return (
                <div style={{ fontSize: 12, marginTop: 4, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }} data-epay-balance-after={after}>
                  <span>{t('aw_epay_balance_after', { amount: `${after < 0 ? '−' : ''}${baht(Math.abs(after))}` })}</span>
                  {after < 0 && <span style={pillAmber}>{t('aw_pc_negative')}</span>}
                </div>
              );
            })()}
          </Field2>
        ) : (
          <Field2 label={t('aw_epay_account')}>
            <select value={accountId} onChange={e => ed(setAccountId, e.target.value)} disabled={!!pickedLine} style={field} data-epay-account>
              <option value="">{t('aw_epay_account_pick')}</option>
              {(opts?.bankAccounts || []).map(a => <option key={a.id} value={a.id}>{a.label}{a.paired ? ` · ${t('aw_epay_account_paired')}` : ''}</option>)}
            </select>
          </Field2>
        )}
        {!cash && opts && !opts.bankAccounts.length && <div style={errBox} data-epay-why="bank_account_required">{t('aw_epay_no_account')}</div>}
        {opts?.payDateRefusal && <div style={errBox} data-epay-why="period_closed">{t('aw_epay_err_period_closed')}</div>}
        {cash && !!boxes.length && !boxes.some(boxOpen) && <div style={{ ...note, color: '#92400E' }} data-epay-why="before_opening">{t('aw_epay_err_before_opening')}</div>}
        {!cash && pickedLine && !lineOk && <div style={{ fontSize: 11.5, color: '#B42318' }}>{t('aw_epay_line_differs', { line: amt(pickedLine.debit), cash: amt(nAmt) })}</div>}
        {cash ? <div style={note} data-epay-cash-no-line>{t('aw_epay_cash_no_line')}</div> : (
          <div>
            <label style={lbl}>{t('aw_pay_from_line')}</label>
            {lines === null ? <Loader /> : (
              <select value={lineId} onChange={e => pickLine(e.target.value)} style={field}>
                <option value="">{t('aw_pay_from_line_none')}</option>
                {lines.map(l => <option key={l.id} value={l.id}>{dmy(l.txn_date)} · {l.account_number} · {l.description} · {amt(l.debit)}</option>)}
              </select>
            )}
          </div>
        )}
        {/* ── (LB-46) The payment's WHT: the payment dialog's per-bill block, for the bill this window makes ── */}
        <div style={{ borderTop: '1px solid var(--border)', paddingTop: 8 }} data-epay-wht="upload">
          <div style={{ fontSize: 11, fontWeight: 800, textTransform: 'uppercase', color: 'var(--text-tertiary)' }}>{t('aw_epay_wht_title')}</div>
          {!withholds ? <div style={note}>{t('aw_epay_wht_none')}</div> : !canWht ? (
            <div style={{ ...note, color: '#92400E' }} data-epay-why="tax_module">{t('aw_epay_wht_face', { face: amt(bill.whtFace), taken: amt(0) })} — {t('aw_epay_wht_needs_tax')}</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 4 }}>
              <div style={note}>{t('aw_epay_wht_face', { face: amt(bill.whtFace), taken: amt(0) })}</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                <Field2 label={t('aw_epay_wht_amount')}><input type="number" step="0.01" min="0" value={wrow.wht} onChange={e => setLine(withWht(wrow, e.target.value))} style={field} data-epay-wht-amount="upload" /></Field2>
                <Field2 label={t('aw_epay_wht_payee')}>
                  <select value={wrow.payeeType} onChange={e => setLine({ payeeType: e.target.value as PayRow['payeeType'] })} style={field}>
                    {!wrow.payeeType && <option value="">{t('aw_epay_wht_payee_pick')}</option>}
                    <option value="company">{t('aw_epay_wht_company')}</option><option value="individual">{t('aw_epay_wht_individual')}</option>
                  </select>
                </Field2>
              </div>
              {wNow > 0 && !!cands && (claimable.length > 0 || cands.anotherRequired) && (
                <div style={{ ...card, padding: '8px 10px', display: 'flex', flexDirection: 'column', gap: 6 }} data-epay-claims="upload">
                  <div style={{ fontSize: 12.5 }}>{t('aw_epay_wht_choose', { period: period(Number(paidDate.slice(0, 4)), Number(paidDate.slice(5, 7))) })}</div>
                  {cands.rows.map(x => (
                    <div key={x.id} style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', fontSize: 12.5 }} data-epay-claim-line={x.id}>
                      {x.claimable
                        ? <input type="radio" name="claim-upload" checked={wrow.mode === 'claim' && wrow.claimLineId === x.id} onChange={() => setLine({ mode: 'claim', claimLineId: x.id, another: false })} aria-label={t('aw_epay_wht_claim')} />
                        : <i className="ti ti-lock" style={{ color: 'var(--text-tertiary)' }} aria-hidden />}
                      <span>{x.claimable ? t('aw_epay_wht_claim') : ''} {x.payeeName} · {amt(x.gross)} × {x.rate}% = <b>{amt(x.wht)}</b></span>
                      {x.filed && <span style={pillGrey}>{t('aw_epay_wht_line_filed')}</span>}
                      {!x.claimable && <span style={note}>{t('aw_epay_wht_line_locked')}</span>}
                      {x.claimable && wrow.mode === 'claim' && wrow.claimLineId === x.id && r2(x.wht) !== r2(wNow) && (
                        <button type="button" onClick={() => setLine({ wht: String(x.wht), whtTouched: true })} style={actBtn}>{t('aw_epay_wht_use_line', { wht: amt(x.wht) })}</button>
                      )}
                    </div>
                  ))}
                  <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 12.5, opacity: cands.canCreate ? 1 : 0.5 }}>
                    <input type="radio" name="claim-upload" disabled={!cands.canCreate} checked={wrow.mode === 'new' && wrow.another} onChange={() => setLine({ mode: 'new', claimLineId: '', another: true })} />
                    {t('aw_epay_wht_another')}
                  </label>
                </div>
              )}
              {wNow > 0 && !!cands && !cands.canCreate && <div style={{ ...note, color: '#92400E' }} data-epay-why="wht_month_filed">{t('aw_epay_wht_month_filed', { period: period(Number(paidDate.slice(0, 4)), Number(paidDate.slice(5, 7))) })}</div>}
              {wNow > 0 && wrow.mode !== 'claim' && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
                  <Field2 label={t('aw_epay_wht_income')}>
                    <select value={wrow.incomeType} onChange={e => setLine(withIncome(wrow, e.target.value, opts?.incomeTypes || []))} style={field} data-epay-wht-income="upload">
                      <option value="">{t('aw_epay_wht_income_pick')}</option>
                      {(opts?.incomeTypes || []).map(x => <option key={x.label} value={x.label}>{x.label} ({x.rate}%)</option>)}
                    </select>
                  </Field2>
                  <Field2 label={t('aw_epay_wht_rate')}><input type="number" step="0.01" min="0" value={wrow.rate} onChange={e => setLine(withRate(wrow, e.target.value))} style={field} /></Field2>
                  <Field2 label={t('aw_epay_wht_gross')}>
                    <input type="number" step="0.01" min="0" value={wrow.gross} onChange={e => setLine({ gross: e.target.value, grossTouched: true })}
                      onBlur={() => { const g = levelGross(wNow, Number(wrow.rate)); if (g !== null && whtOf(Number(wrow.gross), Number(wrow.rate)) !== r2(wNow) && Math.abs(g - Number(wrow.gross)) <= 0.03) setLine({ gross: String(g) }); }}
                      style={field} data-epay-gross="upload" />
                  </Field2>
                </div>
              )}
            </div>
          )}
          {!!whtWhy && (dirty || wNow > 0) && <div style={{ fontSize: 11.5, color: '#B42318', marginTop: 4 }} data-epay-row-why="upload">{whtWhy}</div>}
        </div>
        <Field2 label={t('aw_pay_reference')}><input value={reference} onChange={e => ed(setReference, e.target.value)} maxLength={200} style={field} /></Field2>
        {err && <div style={errBox}>{err}</div>}
      </div>
    </Dialog>
  );
}

/**
 * ── A scan's four doors, one place (batch 7 R3b; lifted out of the record window on owner 2026-11-02) ────────────────────────────────
 * Approve, Return to client, Void record and Return to draft, each its door and its dialog, for the record window and for a list's Status
 * dropdown (useRecordDoors, below): one implementation, so a scan has one set of rules wherever it is worked on. `onDone` gets what the door
 * answered, a void's or a return's `detached` included. `ask`: why this door is shut now, or '': asked at the click by a host whose verdict
 * may be minutes old, because a refused call pages the admins.
 */
export type ScanDoor = 'approve' | 'reject' | 'uvoid' | 'todraft';
function ScanDoorDialog({ ctx, door, recordKey, recordNo, name, vendor, total, review, ask, onClose, onDone }: {
  ctx: AwCtx; door: ScanDoor; recordKey: string; recordNo: string; name: string; vendor: string | null | undefined; total: number | null | undefined; review: AwRecordReview;
  ask?: () => Promise<string>; onClose: () => void; onDone: (out?: any) => void;
}) {
  const { t } = useTranslation();
  if (door === 'approve') return <ApproveDialog ctx={ctx} recordKey={recordKey} recordNo={recordNo} review={review} ask={ask} onClose={onClose} onDone={() => onDone()} />;
  const words = {
    reject: { title: 'aw_er_reject_title', hint: 'aw_er_reject_hint', icon: 'ti ti-arrow-back-up', label: 'aw_er_act_reject', run: () => api.awExpenseRecordReject },
    uvoid: { title: 'aw_er_void_title', hint: 'aw_er_void_hint', icon: 'ti ti-ban', label: 'aw_er_act_void', run: () => api.awExpenseRecordVoid },
    todraft: { title: 'aw_er_to_draft_title', hint: 'aw_er_to_draft_hint', icon: 'ti ti-pencil-off', label: 'aw_er_act_to_draft', run: () => api.awExpenseRecordToDraft },
  }[door];
  return (
    <ReasonDialog title={t(words.title)} docNo={name} line={<><b style={{ color: 'var(--text-primary)' }}>{name}</b> · {vendor || '—'} · {money(total)}</>}
      hint={t(words.hint)} icon={words.icon} label={t(words.label)} onClose={onClose}
      onSubmit={async reason => {
        const why = ask ? await ask() : '';
        if (why) throw new Error(why);
        let out: any;
        try { out = await words.run()(ctx.clientId, recordKey, reason); } catch (e: any) { throw localErr(e, t); }
        onDone(out);
      }} />
  );
}

/** Whether a register row may have a door at all: what puts the caret on its status. Which door, and whether it is open, is GET /doors' to
 *  say as the dropdown opens. A scan with no state left to move, or a bill paid on or void (its Void refuses while a payment stands), stays a plain pill. */
export const recordMayHaveDoors = (r: Pick<AwExpenseRecord, 'upload_id' | 'upload_status' | 'bill_id' | 'bill_status' | 'doc_status'>): boolean =>
  r.doc_status !== 'voided' && ((!!r.upload_id && (r.upload_status === 'pending' || r.upload_status === 'approved')) || (!!r.bill_id && r.bill_status === 'open'));

/**
 * ── A record's doors on a list's Status dropdown (owner 2026-11-02, "Standardized Document Table Layout") ─────────────────────────────
 * The doors the record window offers, under the window's conditions, for the two lists that hold scans. Every gate is the server's `review`,
 * read as the dropdown opens: one that is offered and refused is greyed with its sentence, one that is not offered is not drawn. A bill with
 * no scan door to end it keeps its own Void, greyed with whatever its door would answer (a payment, a booked note, its line in the VAT return). Pay, Book and the
 * rest stay in the window: they do not move a status.
 */
export function useRecordDoors(ctx: AwCtx) {
  const { t } = useTranslation();
  const [open, setOpen] = useState<{ door: ScanDoor | 'void'; key: string; name: string; v: AwRecordDoors } | null>(null);
  const [notice, setNotice] = useState('');
  const close = () => setOpen(null);
  const done = (out?: any) => { setNotice(out?.detached ? t('aw_er_detached_note') : ''); setOpen(null); ctx.bump(); };
  /** Why a bill's Void is shut, in the reader's language: each thing its door would refuse (GET /doors `billVoid`), and a line posted twice, which the window greys too. */
  const billVoidWhy = (v: AwRecordDoors): string => {
    const r = v.billVoid;
    if (r?.code === 'payments') return t('aw_list_bill_has_payments');
    if (r?.code === 'tax_module') return t('aw_list_bill_void_tax');
    if (r?.code === 'notes_booked') return t('aw_pn_bill_locked');
    if (r) return unbookWhy({ code: r.code, why: r.why, entry: r.entry, asset: r.asset, closed: r.closed }, t);
    return v.record?.chips?.some(c => c.code === 'posted_twice') ? t('aw_er_void_locked') : '';
  };
  const gateOf = (v: AwRecordDoors | null | undefined, door: ScanDoor): AwReviewGate | null | undefined => {
    const rv = v?.review;
    return !rv ? null : door === 'approve' ? rv.approve : door === 'reject' ? rv.reject : door === 'uvoid' ? rv.void : rv.toDraft;
  };
  const items = (key: string, name: string, v: AwRecordDoors | null, readError: string): RowMenuEntry[] => {
    if (readError) return [{ key: 'failed', note: readError }];
    if (!v) return [{ key: 'reading', note: t('aw_list_reading') }];
    if (!v.found) return [{ key: 'gone', note: t('aw_doc_menu_gone') }];
    const scan = (door: ScanDoor, label: string, icon: string, danger = false): RowMenuEntry[] => {
      const g = gateOf(v, door);
      return g?.offered ? [{ key: door, icon, danger, label: t(label), withheld: g.refusal ? whyText(g, t) : undefined, onPick: () => setOpen({ door, key, name, v }) }] : [];
    };
    const out: RowMenuEntry[] = [
      ...scan('approve', 'aw_er_act_approve', 'ti-circle-check'),
      ...scan('reject', 'aw_er_act_reject', 'ti-arrow-back-up'),
      ...scan('todraft', 'aw_er_act_to_draft', 'ti-pencil-off'),
      ...scan('uvoid', 'aw_er_act_void', 'ti-ban', true),
    ];
    const b = v.bill;
    if (b && (b.status === 'open' || b.status === 'partial') && !v.review?.void.offered) {
      out.push({ key: 'void', icon: 'ti-ban', danger: true, label: t('aw_pay_void_btn'), onPick: () => setOpen({ door: 'void', key, name: b.bill_no, v }),
        withheld: billVoidWhy(v) || undefined });
    }
    return out.length ? out : [{ key: 'none', note: t('aw_list_status_no_door') }];
  };
  const menu = (key: string, name: string) => ({
    read: (): Promise<AwRecordDoors> => api.awRowDoors(ctx.clientId, 'expense', key),
    items: (v: AwRecordDoors | null, readError: string) => items(key, name, v, readError),
  });
  // The verdict may be minutes old by the click: asked again. A record gone meanwhile, or a door shut, is a sentence and no request.
  const ask = (door: ScanDoor, key: string, name: string) => async (): Promise<string> => {
    const v: AwRecordDoors = await api.awRowDoors(ctx.clientId, 'expense', key);
    const g = gateOf(v, door);
    if (!v.found || !g?.offered) { ctx.bump(); return t('aw_list_status_moved', { no: name }); }
    return g.refusal ? whyText(g, t) : '';
  };
  const askBill = async (key: string, name: string): Promise<string> => {
    const v: AwRecordDoors = await api.awRowDoors(ctx.clientId, 'expense', key);
    if (!v.found || !v.bill || (v.bill.status !== 'open' && v.bill.status !== 'partial')) { ctx.bump(); return t('aw_list_status_moved', { no: name }); }
    return billVoidWhy(v);
  };
  let dialog: React.ReactNode = null;
  if (open?.door === 'void' && open.v.bill) {
    const p = open.v.bill;
    dialog = (
      <ReasonDialog title={t('aw_pay_void_title')} docNo={p.bill_no} line={<><b style={{ color: 'var(--text-primary)' }}>{p.bill_no}</b> · {p.vendor_name} · {amt(p.to_pay)} {p.currency}</>}
        hint={t('aw_pay_void_hint')} icon="ti ti-ban" label={t('aw_pay_void_btn')} onClose={close}
        onSubmit={async reason => { const why = await askBill(open.key, open.name); if (why) throw new Error(why); await api.awPayableVoid(ctx.clientId, p.id, reason); done(); }} />
    );
  } else if (open && open.door !== 'void' && open.v.review) {
    const r = open.v.record;
    dialog = <ScanDoorDialog ctx={ctx} door={open.door} recordKey={open.key} recordNo={r?.expense_no || ''} name={open.name} vendor={r?.supplier} total={r?.total} review={open.v.review}
      ask={ask(open.door, open.key, open.name)} onClose={close} onDone={done} />;
  }
  return { menu, dialog, notice: notice ? <div style={{ ...card, padding: '9px 12px', fontSize: 12.5, color: '#92400E' }} data-notice="detached"><i className="ti ti-info-circle" aria-hidden /> {notice}</div> : null };
}

/**
 * ── The record, full screen (EXP-16 / FS-8) ─────────────────────────────────────────────────────────
 * Headed with the EXP number (the supplier's own number when the record has none). A bill is FULLY
 * workable here — pay, note, edit, book / unbook, void, delete, what it posted — through the Payments
 * drawer's own sections and dialogs, each opening full screen over this window (batch 12 tail T2; Esc closes the top
 * one only). An upload shows its sheets
 * inline (sourceDoc.tsx ScanSheetView — ⛔ never a new tab) with an explicit Download, and — batch 7 R3b —
 * its own doors: Approve (full screen, an account per item), Return to client, Return to draft, Void
 * record and Pay (a bill made from it and its payment in one step), besides "Create bill". Which of them
 * the scan's state allows, and why one cannot be done now, is the server's answer (`review` — the same
 * refusal functions its routes run): an action that cannot be done is never a greyed button, it is a
 * sentence naming the reason. Where the bill, the VAT line and the scan's OCR disagree, the field is
 * marked. Read-only itself: every write is one of the dialogs it opens.
 */
export function ExpenseRecordWindow({ ctx, recordKey, onClose }: { ctx: AwCtx; recordKey: string; onClose: () => void }) {
  const { t } = useTranslation();
  const [key, setKey] = useState(recordKey);
  const [d, setD] = useState<AwExpenseRecordDetail | null>(null);
  const [loadErr, setLoadErr] = useState('');
  const { busy, err, act } = usePayableActs(ctx);
  const [dlg, setDlg] = useState<{ kind: 'pay' | 'note' | 'edit' | 'void'; p: AwPayable } | { kind: 'create' | 'approve' | 'reject' | 'uvoid' | 'todraft' | 'upay' } | null>(null);
  const [entry, setEntry] = useState('');
  const [notice, setNotice] = useState('');
  const [payPage, setPayPage] = useState(1);   // the bill's payment history, 100 a page (batch 11 R5)
  // A bill deleted from this window may take its record with it (a bill with no number and no upload):
  // the re-read then answers 404 and the window closes instead of reading "not found" at the operator.
  const goneOk = useRef(false);
  useEffect(() => { setKey(recordKey); }, [recordKey]);
  useEffect(() => {
    let alive = true;
    setLoadErr('');
    api.awExpenseRecord(ctx.clientId, key, payPage)
      .then((x: AwExpenseRecordDetail) => { if (alive) setD(x); })
      .catch((e: any) => {
        if (!alive) return;
        if (e?.status === 404 && goneOk.current) { onClose(); return; }
        setD(null); setLoadErr(e?.status === 404 ? t('aw_pay_err_not_found') : `${t('aw_er_load_failed')}: ${String(e?.message || e)}`);
      });
    return () => { alive = false; };
  }, [ctx.clientId, key, ctx.reload, payPage]);   // eslint-disable-line react-hooks/exhaustive-deps
  const r = d?.record;
  const bill = d?.bill || null;
  const p = bill && !bill.deleted ? bill.payable : null;
  const up = d?.upload || null;
  const rv = d?.review || null;
  const done = () => { setDlg(null); ctx.bump(); };
  // A void / return to draft that only LET GO of a VAT line another record still holds says so (decision (b)).
  const doneScan = (out: any) => { setNotice(out?.detached ? t('aw_er_detached_note') : ''); done(); };
  const can = (g?: AwReviewGate) => !!g && g.offered && !g.refusal;
  // ⛔ The books seat (and the auditor's) READS: middleware.ts refuses its every non-GET with a JSON 403, which
  // the finish hook pages the admins with. So nothing that writes is drawn for it — Documents.tsx's rule — and
  // the server sends it no `review` (no scan door, and no "needs the Tax module" it could never act on).
  const readOnly = isReadOnlySeat(ctx);
  const voidLocked = r?.chips.some(c => c.code === 'posted_twice') ? t('aw_er_void_locked') : '';
  const canCreate = !readOnly && !!up && !bill && !up.withdrawn && r?.doc_status !== 'voided' && (up.status === 'pending' || up.status === 'approved');
  const docNo = r ? (r.expense_no || r.supplier_no) : '';
  const del = (x: AwPayable) => {
    if (!window.confirm(t('aw_pay_delete_confirm', { no: x.bill_no }))) return;
    goneOk.current = true;
    act(`del-${x.id}`, () => api.awPayableDelete(ctx.clientId, x.id));
  };
  const footer = r ? (
    <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
      {p && !readOnly && (
        <PayableActions notes={bill?.notes} ctx={ctx} p={p} busy={busy} act={act} onPay={x => setDlg({ kind: 'pay', p: x })} onNote={x => setDlg({ kind: 'note', p: x })}
          onEdit={x => setDlg({ kind: 'edit', p: x })} onBooked={() => undefined} onVoid={x => setDlg({ kind: 'void', p: x })} onDelete={del} voidLocked={voidLocked} recordKey={key} bookRefusal={d?.bill?.bookRefusal}
          settleLock={d?.bill?.settleLock} bookBlock={d?.bill?.bookBlock} unbookRefusal={d?.bill?.unbookRefusal} />
      )}
      {p && readOnly && <DocEntriesBtn ctx={ctx} kind="expense" id={key} docNo={docNo} />}
      {!p && canCreate && (
        <button onClick={() => setDlg({ kind: 'create' })} style={{ ...actBtn, borderColor: 'var(--accent)', color: 'var(--accent-text)' }}>
          <i className="ti ti-file-plus" aria-hidden /> {t('aw_pay_create_from_upload')}
        </button>
      )}
      {!p && up && <DocEntriesBtn ctx={ctx} kind="expense" id={key} docNo={docNo} />}
      {/* Batch 9 R4 (COA-H7): register a fixed asset bought on this expense. Offered on any record that
          is in the books — the asset form asks the server which of its lines can carry one and says
          why a line cannot (booked to an expense, already spent), so the rule is not guessed here. The
          jump goes through the shell (ctx.setNav), closing this window in the same URL write. */}
      {!readOnly && !!r.line_id && r.doc_status !== 'voided' && (
        <button type="button" data-create-asset onClick={() => ctx.setNav('accounting', 'fixed_assets', { fatab: 'register', fasrc: recordKey, fa: '', exp: '' })} style={actBtn}>
          <i className="ti ti-building-warehouse" aria-hidden /> {t('aw_er_create_asset')}
        </button>
      )}
      {/* The scan's own doors (batch 7 R3b) — each only when the server says it can be done now. */}
      {rv && can(rv.approve) && (
        <button onClick={() => setDlg({ kind: 'approve' })} style={{ ...btnPrimary, padding: '5px 11px', fontSize: 12 }}>
          <i className="ti ti-circle-check" aria-hidden /> {t('aw_er_act_approve')}
        </button>
      )}
      {rv && can(rv.pay) && (
        <button onClick={() => setDlg({ kind: 'upay' })} style={actBtn}><i className="ti ti-cash" aria-hidden /> {t('aw_er_act_pay')}</button>
      )}
      {rv && can(rv.reject) && (
        <button onClick={() => setDlg({ kind: 'reject' })} style={actBtn}><i className="ti ti-arrow-back-up" aria-hidden /> {t('aw_er_act_reject')}</button>
      )}
      {rv && can(rv.toDraft) && (
        <button onClick={() => setDlg({ kind: 'todraft' })} style={actBtn}><i className="ti ti-pencil-off" aria-hidden /> {t('aw_er_act_to_draft')}</button>
      )}
      {rv && can(rv.void) && (
        <button onClick={() => setDlg({ kind: 'uvoid' })} style={{ ...actBtn, color: '#B42318' }}><i className="ti ti-ban" aria-hidden /> {t('aw_er_act_void')}</button>
      )}
    </div>
  ) : null;
  // Why an action that belongs to the scan's state cannot be done now — words, not a greyed button.
  const blocked = rv ? ([['aw_er_act_approve', rv.approve], ['aw_er_act_reject', rv.reject], ['aw_er_act_to_draft', rv.toDraft], ['aw_er_act_void', rv.void]] as [string, AwReviewGate][])
    .filter(([, g]) => g.offered && !!g.refusal) : [];
  return (
    <Dialog fullscreen title={t('aw_er_window_title')} docNo={docNo} onClose={onClose} footer={footer}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {(loadErr || err) && <div style={errBox}>{loadErr || err}</div>}
        {notice && <div style={{ ...card, padding: '9px 12px', fontSize: 12.5, color: '#92400E' }} data-notice="detached"><i className="ti ti-info-circle" aria-hidden /> {notice}</div>}
        {/* Batch 11 R1 (COA-D9): the record's entries still in the books — read-only; every door below keeps its state. */}
        <InBooksBanner rows={d?.inBooks} who="staff" />
        {!d && !loadErr && <Loader />}
        {r && (
          <>
            <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', flexWrap: 'wrap' }}>
              <StatusPills r={r} />
              <div style={{ flex: '1 1 300px', minWidth: 0 }}><RecordChips r={r} onEntry={e => setEntry(e.id)} onRecord={k => setKey(k)} /></div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: up ? 'minmax(0, 1fr) minmax(0, 1fr)' : 'minmax(0, 1fr)', gap: 14, alignItems: 'start' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12, minWidth: 0 }}>
                <div>
                  <div style={sectionHead}>{t('aw_er_facts_title')}</div>
                  <div style={{ ...card, display: 'flex', flexWrap: 'wrap', gap: 16 }}>
                    <Fact label={t('aw_er_col_no')}>
                      {r.expense_no ? <b style={{ fontVariantNumeric: 'tabular-nums' }}>{r.expense_no}</b> : <span title={t('aw_er_no_number')} style={{ color: 'var(--text-tertiary)' }}>—</span>}
                    </Fact>
                    <Fact label={t('aw_er_col_date')}>{r.date ? dmy(r.date) : '—'}</Fact>
                    <Fact label={t('aw_er_col_supplier')}>{r.supplier || '—'}{r.supplier_tax_id ? <span style={{ color: 'var(--text-tertiary)' }}> · {r.supplier_tax_id}</span> : ''}</Fact>
                    <Fact label={t('aw_er_col_supplier_no')}><span style={{ fontVariantNumeric: 'tabular-nums' }}>{r.supplier_no || '—'}</span></Fact>
                    <Fact label={t('aw_er_col_category')}>
                      {r.category ? `${r.category.code} · ${r.category.name_th || r.category.name_en}` : r.category_count > 1 ? t('aw_er_category_multiple', { n: r.category_count }) : <span style={{ color: 'var(--text-tertiary)' }} title={t('aw_er_category_none')}>—</span>}
                    </Fact>
                    <Fact label={t('aw_er_col_total')}><b style={{ fontVariantNumeric: 'tabular-nums' }}>{money(r.total)} {r.currency}</b>
                      {r.to_pay !== null && <div style={{ fontSize: 11.5, color: 'var(--text-tertiary)' }}>{t('aw_er_to_pay')}: {amt(r.to_pay)} {r.currency}</div>}
                    </Fact>
                    <Fact label={t('aw_er_f_vat')}><span style={{ fontVariantNumeric: 'tabular-nums' }}>{money(r.vat)}</span></Fact>
                    <Fact label={t('aw_er_col_paid')}><span style={{ fontVariantNumeric: 'tabular-nums' }}>{money(r.paid)}</span></Fact>
                    <Fact label={t('aw_er_col_outstanding')}><span style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 700 }}>{money(r.outstanding)}</span></Fact>
                  </div>
                  <div style={{ ...note, marginTop: 4 }}>{SOURCE_KEY[r.source] ? t(SOURCE_KEY[r.source]) : r.source}</div>
                </div>
                {d.compare.filter(c => [c.bill, c.line, c.ocr].filter(v => v !== null && v !== '').length > 1).length > 0 && (
                  <div>
                    <div style={sectionHead}>{t('aw_er_compare_title')}</div>
                    <div style={{ ...card, padding: 0, overflowX: 'auto' }}>
                      <table style={table}>
                        <thead><tr>
                          <th style={th}>{t('aw_er_compare_field')}</th>
                          {bill && <th style={th}>{t('aw_er_compare_bill')}</th>}
                          {d.line && <th style={th}>{t('aw_er_compare_line')}</th>}
                          {up && <th style={th}>{t('aw_er_compare_ocr')}</th>}
                          <th style={th} aria-hidden />
                        </tr></thead>
                        <tbody>
                          {d.compare.map(c => (
                            <tr key={c.field} data-differs={c.differs ? '1' : undefined} style={c.differs ? { background: '#FFFBEB' } : undefined}>
                              <td style={{ ...td, fontWeight: 700 }}>{COMPARE_KEY[c.field] ? t(COMPARE_KEY[c.field]) : c.field}</td>
                              {bill && <td style={td}>{showCell(c, c.bill)}</td>}
                              {d.line && <td style={td}>{showCell(c, c.line)}</td>}
                              {up && <td style={td}>{showCell(c, c.ocr)}</td>}
                              <td style={td}>{c.differs && <span style={pillAmber}>≠ {t('aw_er_compare_differs')}</span>}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <div style={{ ...note, marginTop: 4 }}>{t('aw_er_compare_hint')}</div>
                  </div>
                )}
                {d.line && (
                  <div style={{ ...card, padding: '9px 12px', fontSize: 12.5 }}>
                    <i className="ti ti-receipt-tax" style={{ color: 'var(--accent)' }} aria-hidden /> {t('aw_er_line_in', { period: `${String(d.line.month).padStart(2, '0')}/${d.line.year}` })}
                    {!d.line.live && <div style={{ ...note, color: '#92400E' }}>{t('aw_er_line_withdrawn')}</div>}
                  </div>
                )}
                <div>
                  <div style={sectionHead}>{t('aw_er_bill_title')}</div>
                  {!bill ? <div style={{ ...card, ...note }}>{t('aw_er_no_bill')}</div> : bill.deleted ? (
                    <div style={card}><PayableFacts p={bill.payable} /><div style={{ ...note, marginTop: 8 }}>{t('aw_er_bill_deleted')}</div></div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                      <div style={card}><PayableFacts p={bill.payable} /></div>
                      <PayablePayments ctx={ctx} p={bill.payable} d={bill} busy={busy} act={act} readOnly={readOnly} onPage={setPayPage} />
                      <PayableNotes ctx={ctx} p={bill.payable} d={bill} busy={busy} act={act} readOnly={readOnly} />
                      <PayableBankLines ctx={ctx} d={bill} busy={busy} act={act} readOnly={readOnly} />
                    </div>
                  )}
                </div>
              </div>
              {up && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10, minWidth: 0 }}>
                  <div style={sectionHead}>{t('aw_er_scan_title')}</div>
                  <div style={{ ...card, display: 'flex', flexWrap: 'wrap', gap: 14, fontSize: 12.5 }}>
                    {up.cancelled
                      ? <span style={pillRed}>{t('aw_er_chip_cancelled')}</span>
                      : <span style={up.status === 'approved' ? pillGreen : up.status === 'rejected' ? pillRed : pillAmber}>{SCAN_KEY[up.status] ? t(SCAN_KEY[up.status]) : up.status}</span>}
                    <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={up.filename}><i className="ti ti-paperclip" aria-hidden /> {up.filename}</span>
                    {/* Off the Supplier bills list since the seven columns (owner 2026-11-02), so here: when it came in, and the VAT-able base as the scan read it. */}
                    <span data-scan-uploaded="" style={{ whiteSpace: 'nowrap' }}>{t('aw_exp_uploaded')}: {fmtDateTime(Number(up.uploaded_at))}</span>
                    <span data-scan-base="" style={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>{t('aw_er_f_base')}: {amt(up.amount)}</span>
                    {!!up.reject_reason && <span style={{ color: '#B42318' }}>{up.reject_reason}</span>}
                  </div>
                  {up.withdrawn ? <div style={{ ...card, ...note }}>{t('aw_er_upload_withdrawn')}</div> : (
                    <ScanSheetView clientId={ctx.clientId} id={up.id} mime={String(up.mime || '')} filename={up.filename} pageCount={Number(up.page_count) || 1}
                      tools={sheet => (
                        /* ⛔ An explicit DOWNLOAD — the file saved, never a new tab; it spins while it fetches. */
                        <DlBtn style={{ ...btn, padding: '4px 9px', fontSize: 12 }} title={t('aw_er_download_title')}
                          onRun={() => api.awExpenseFileDownload(ctx.clientId, up.id, sheet > 1 ? sheet : undefined, up.filename)}>
                          <i className="ti ti-download" aria-hidden /> {t('aw_er_download')}
                        </DlBtn>
                      )} />
                  )}
                  {up.cancelled && (
                    <div style={{ ...card, padding: '9px 12px', fontSize: 12.5, color: '#B42318' }}>
                      {t('aw_er_cancelled_by', { by: up.cancelled.by || '—', when: fmtDateTime(up.cancelled.at), reason: up.cancelled.reason || '—' })}
                    </div>
                  )}
                  {blocked.map(([k, g]) => (
                    <div key={k} data-why={g.refusal?.code} style={{ ...card, padding: '9px 12px', display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 12.5 }}>
                      <i className="ti ti-info-circle" style={{ color: '#92400E', flexShrink: 0, marginTop: 2 }} aria-hidden />
                      <div><b>{t(k)}</b> — {whyText(g, t)}</div>
                    </div>
                  ))}
                  <div style={{ ...card, padding: '9px 12px', display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                    <i className="ti ti-lock" style={{ color: 'var(--accent)', flexShrink: 0, marginTop: 1 }} aria-hidden />
                    <div style={note}>{t('aw_er_upload_readonly')}</div>
                  </div>
                </div>
              )}
            </div>
          </>
        )}
        {r && <RecordEvidence clientId={ctx.clientId} kind="expense" id={key} />}
        {dlg?.kind === 'pay' && <PaymentDialog ctx={ctx} bills={[dlg.p]} onClose={() => setDlg(null)} onDone={done} />}
        {dlg?.kind === 'note' && <NoteDialog ctx={ctx} payable={dlg.p} onClose={() => setDlg(null)} onDone={done} />}
        {dlg?.kind === 'edit' && <BillDialog ctx={ctx} payable={dlg.p} onClose={() => setDlg(null)} onSaved={done} />}
        {dlg?.kind === 'create' && up && <BillDialog ctx={ctx} upload={up} onClose={() => setDlg(null)} onSaved={done} />}
        {dlg?.kind === 'void' && (
          <ReasonDialog title={t('aw_pay_void_title')} docNo={dlg.p.bill_no} line={<><b style={{ color: 'var(--text-primary)' }}>{dlg.p.bill_no}</b> · {dlg.p.vendor_name} · {amt(dlg.p.to_pay)} {dlg.p.currency}</>}
            hint={t('aw_pay_void_hint')} icon="ti ti-ban" label={t('aw_pay_void_btn')} onClose={() => setDlg(null)}
            onSubmit={async reason => { await api.awPayableVoid(ctx.clientId, dlg.p.id, reason); done(); }} />
        )}
        {(dlg?.kind === 'approve' || dlg?.kind === 'reject' || dlg?.kind === 'uvoid' || dlg?.kind === 'todraft') && up && rv && (
          <ScanDoorDialog ctx={ctx} door={dlg.kind} recordKey={key} recordNo={r?.expense_no || ''} name={docNo || up.filename} vendor={up.vendor} total={up.total} review={rv} onClose={() => setDlg(null)} onDone={doneScan} />
        )}
        {dlg?.kind === 'upay' && up && <PayUploadDialog ctx={ctx} recordKey={key} recordNo={docNo || up.filename} uploadId={up.id} onClose={() => setDlg(null)} onDone={() => { setNotice(''); done(); }} />}
        {entry && <EntryDetailDialog entryId={entry} onClose={() => setEntry('')} />}
      </div>
    </Dialog>
  );
}

// ── The register ────────────────────────────────────────────────────────────────────────────────
function RecordsRegister({ ctx }: { ctx: AwCtx }) {
  const { t } = useTranslation();
  const rec = useRecordParam();
  const [params, setParams] = useSearchParams();
  const [doc, setDoc] = useState<string>('');
  // ⛔ The payment filter is in the URL, not only in this component. The Dashboard's AP cards count
  // what is still owed and link here; if the filter were local state the reader would land on EVERY
  // expense and the number they clicked would not be the number on the screen. It is also where the
  // retired Payments submenu's address lands (menus.ts MOVED → pay=unpaid). An unknown value is
  // ignored rather than trusted — the query string is typed by anyone.
  const payParam = params.get('pay') || '';
  const [pay, setPayState] = useState<string>((PAY_FILTERS as readonly string[]).includes(payParam) ? payParam : '');
  const setPay = (v: string) => {
    setPayState(v);
    setParams(prev => {
      const n = new URLSearchParams(prev);
      if (v) n.set('pay', v); else n.delete('pay');
      return n;
    }, { replace: true });
  };
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [q, setQ] = useState('');
  const [qd, setQd] = useState('');
  const [page, setPage] = useState(1);
  // Batch 12 tail T3 (FS-11): the register's sort through the one hook — the same keys, default (date, newest first)
  // and first-click rule as before, now kept for this viewer across closing, submenus, periods and reloads; the page
  // back to 1 on a change, as before. Sent on every request, as before (the server's whitelist is unchanged).
  const ls = useListSort(ctx, 'expenses.records', { onChange: () => setPage(1) });
  const { sort, dir } = ls;
  const [limit, setLimit] = useState(getPageSize());
  const [data, setData] = useState<AwExpenseRecordPage | null>(null);
  const [err, setErr] = useState('');
  const [creating, setCreating] = useState(false);
  const [entry, setEntry] = useState('');
  // "Record one payment" (batch 11 R5, EXP-12, design §3.2.7): the ticked records' bills, paid in one payment. Only a
  // record whose bill is live and owes something can be ticked, and never by a read-only seat.
  const [picked, setPicked] = useState<string[]>([]);
  const [paying, setPaying] = useState(false);
  const ro = isReadOnlySeat(ctx);
  useEffect(() => { const h = setTimeout(() => { setQd(q); setPage(1); }, 220); return () => clearTimeout(h); }, [q]);
  useEffect(() => {
    let alive = true;
    api.awExpenseRecords(ctx.clientId, { doc, pay, from, to, q: qd, sort, dir, page, limit })
      .then((x: AwExpenseRecordPage) => { if (alive) { setData(x); setErr(''); } })
      .catch((e: any) => { if (alive) { setData({ rows: [], total: 0, page: 1, limit, summary: { byCurrency: [], unlinkedInput: 0 } }); setErr(String(e?.message || e)); } });
    return () => { alive = false; };
  }, [ctx.clientId, ctx.reload, doc, pay, from, to, qd, sort, dir, page, limit]);   // eslint-disable-line react-hooks/exhaustive-deps
  const rows = data?.rows || [];
  const payable = (r: AwExpenseRecord) => !!r.bill_id && (r.bill_status === 'open' || r.bill_status === 'partial');
  // The record's doors on its Status dropdown (owner 2026-11-02): the window's own, under its conditions.
  const doors = useRecordDoors(ctx);
  const toggle = (id: string) => setPicked(p => (p.includes(id) ? p.filter(x => x !== id) : [...p, id]));
  const summary = data?.summary?.byCurrency || [];
  const unlinked = Number(data?.summary?.unlinkedInput || 0);
  const filters = (
    <>
      <input value={q} onChange={e => setQ(e.target.value)} placeholder={t('aw_er_search_ph')} aria-label={t('aw_er_search_ph')} style={{ ...inp, flex: 'none', width: 260 }} />
      {/* Outstanding left the headers for the Status cell (owner 2026-11-02): "what is owed most" stays a click (listSort.tsx LIST's noHeader). */}
      <SortChip k="outstanding" s={ls} label={t('aw_col_outstanding')} />
      <span style={{ width: 1, height: 22, background: 'var(--border-strong)', flex: 'none' }} aria-hidden />
      <span style={{ fontSize: 11, fontWeight: 800, color: 'var(--text-tertiary)', whiteSpace: 'nowrap' }}>{t('aw_er_doc_filter')}</span>
      {DOC_FILTERS.map(s => (
        <button key={s || 'all'} type="button" aria-pressed={s === doc} onClick={() => { setDoc(s); setPage(1); }}
          style={{ ...(s === doc ? { ...chip, ...chipOn } : chip), flex: 'none', whiteSpace: 'nowrap' }}>
          {s ? t(DOC_KEY[s]) : t('aw_er_doc_all')}
        </button>
      ))}
      <span style={{ width: 1, height: 22, background: 'var(--border-strong)', flex: 'none' }} aria-hidden />
      <label style={{ fontSize: 11, fontWeight: 800, color: 'var(--text-tertiary)', whiteSpace: 'nowrap' }}>
        {t('aw_er_pay_filter')}{' '}
        <select value={pay} onChange={e => { setPay(e.target.value); setPage(1); }} aria-label={t('aw_er_pay_filter')} style={{ ...inp, padding: '5px 8px' }}>
          {PAY_FILTERS.map(s => <option key={s || 'all'} value={s}>{s ? t(PAY_KEY[s]) : t('aw_er_pay_all')}</option>)}
        </select>
      </label>
      <span style={{ width: 1, height: 22, background: 'var(--border-strong)', flex: 'none' }} aria-hidden />
      <input type="date" value={from} onChange={e => { setFrom(e.target.value); setPage(1); }} title={t('aw_er_from')} aria-label={t('aw_er_from')} style={{ ...inp, flex: 'none' }} />
      <span style={{ color: 'var(--text-tertiary)' }}>–</span>
      <input type="date" value={to} onChange={e => { setTo(e.target.value); setPage(1); }} title={t('aw_er_to')} aria-label={t('aw_er_to')} style={{ ...inp, flex: 'none' }} />
      {(from || to) && <button type="button" onClick={() => { setFrom(''); setTo(''); setPage(1); }} title={t('aw_er_clear_dates')} aria-label={t('aw_er_clear_dates')} style={{ ...actBtn, padding: '4px 7px', flex: 'none' }}><i className="ti ti-x" aria-hidden /></button>}
    </>
  );
  // ⛔ The sheet answers the question the screen is showing: the same filters and the same sort.
  // PeakToolbar's right side never folds and takes its width from the filters, which hold the only search box. So on a
  // phone (2026-11-02, global.css .pk-bar-icons-phone) these buttons are icons with their titles: with New beside Export
  // and their words showing, the strip fell to about 66px at 375, and to nothing with a record ticked. The maxWidth (72 =
  // the page's gutters and the toolbar's padding) still keeps the page from moving sideways.
  const exportBtn = (
    <span className="pk-bar-icons-phone" style={{ display: 'inline-flex', gap: 8, maxWidth: 'calc(100vw - 72px)', overflowX: 'auto' }}>
      {!ro && picked.length > 0 && (
        <button type="button" onClick={() => setPaying(true)} title={t('aw_epay_record_one', { n: picked.length })} style={{ ...btnPrimary, padding: '5px 11px', fontSize: 12 }} data-epay-record-one>
          <i className="ti ti-cash" aria-hidden /> {t('aw_epay_record_one', { n: picked.length })}
        </button>
      )}
      <DlBtn style={btn} title={t('aw_export_title')} onRun={() => api.awExpenseRecordsExport(ctx.clientId, { doc, pay, from, to, q: qd, sort, dir })}>
        <i className="ti ti-file-spreadsheet" aria-hidden /> {t('aw_export')}
      </DlBtn>
      {/* ➕ New = a bill typed in, full screen (the Payments pane's own form), beside Export (owner 2026-11-02, NewDocBtn.tsx).
          Not for a login that only reads the books (the window's rule, above). */}
      {!ro && <NewDocBtn onClick={() => setCreating(true)} title={t('aw_pay_new_title')} />}
    </span>
  );
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {/* ⛔ One card per currency, never a total across them — apByCurrency, the Payments pane's own. */}
      {summary.length > 0 && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {summary.map(s => (
            <Stat key={s.currency} label={`${t('aw_pay_sum_outstanding')} · ${s.currency}`} value={amt(s.outstanding)}
              sub={`${t('aw_pay_sum_open', { n: s.open_count })}${Number(s.overdue) ? ` · ${t('aw_pay_sum_overdue')}: ${amt(s.overdue)}` : ''}`}
              tone={Number(s.overdue) ? '#92400E' : undefined} />
          ))}
        </div>
      )}
      {/* ⛔ A COUNT, never a link (EXP-15). These are input VAT lines typed straight into the
          bookkeeping module's own ledger — the way in that predates this register and is still
          correct. Offering somewhere to click would say they belong here; the number is here only
          so nobody reads this register as being the whole VAT return. */}
      {unlinked > 0 && (
        <div style={{ ...card, padding: '9px 12px', display: 'flex', gap: 8, alignItems: 'flex-start' }} data-er-unlinked={unlinked}>
          <i className="ti ti-info-circle" style={{ color: 'var(--text-secondary)', flexShrink: 0, marginTop: 1 }} aria-hidden />
          <div style={{ fontSize: 12.5, color: 'var(--text-secondary)' }}>{t('aw_er_unlinked_input', { n: unlinked })}</div>
        </div>
      )}
      {doors.notice}
      <div style={{ ...card, padding: 0, overflow: 'visible' }}>
        <PeakToolbar filters={filters} right={exportBtn} />
        {err && <div style={{ ...errBox, margin: 12 }}>{err}</div>}
        <div style={{ overflowX: 'auto' }}>
          {data === null ? <Loader /> : !rows.length ? <div style={emptyLine}>{t('aw_er_none')}</div> : (
            <table style={table}>
              <thead>
                <tr>
                  {/* Seven columns (owner 2026-11-02, "Standardized Document Table Layout"), where "the customer's columns" of testExpenseRecords
                      stood: the tick and the supplier's number are in the number's cell, both statuses and Paid and Outstanding in the Status cell,
                      and the category is the record's window. The module-level SortTh (listSort.tsx): the header keeps the keyboard focus a click left on it. */}
                  <SortTh k="expense_no" s={ls} label={t('aw_col_no')} />
                  <SortTh k="date" s={ls} label={t('aw_col_date')} />
                  <SortTh k="supplier" s={ls} label={t('aw_er_col_supplier')} />
                  <SortTh k="total" s={ls} align="right" label={t('aw_col_total')} />
                  <th style={th}>{t('aw_col_currency')}</th>
                  <th style={th}>{t('aw_col_status')}</th>
                  <th style={th}>{t('aw_col_actions')}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(r => (
                  <tr key={r.key} data-key={r.key} onClick={() => rec.open(r.key)} title={t('aw_er_open_title')} style={{ cursor: 'pointer' }}>
                    <NoCell sub={r.supplier_no || undefined} rel={{ ctx, kind: 'expense', id: r.key, docNo: r.expense_no || r.supplier_no, count: r.linked_count }}
                      lead={!ro && payable(r) && <input type="checkbox" checked={picked.includes(r.bill_id)} onClick={e => e.stopPropagation()} onChange={() => toggle(r.bill_id)} aria-label={t('aw_epay_select')} title={t('aw_epay_select')} data-epay-pick={r.key} style={{ margin: '0 6px 0 0', verticalAlign: 'middle' }} />}
                      no={r.expense_no ? <span style={{ color: 'var(--accent)' }}>{r.expense_no}</span> : <NoNumber title={t('aw_er_no_number')} />} />
                    <td style={{ ...td, whiteSpace: 'nowrap' }}>{r.date ? dmy(r.date) : '—'}</td>
                    <td style={td}>{r.supplier || '—'}{r.supplier_tax_id && <div style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>{r.supplier_tax_id}</div>}</td>
                    <td style={num}>
                      {money(r.total)}
                      {r.to_pay !== null && <div style={{ fontSize: 11, color: 'var(--text-tertiary)' }} title={t('aw_er_to_pay')}>{t('aw_er_to_pay')}: {amt(r.to_pay)}</div>}
                    </td>
                    <td style={td}>{r.currency}</td>
                    <StatusCell<AwRecordDoors> word={DOC_KEY[r.doc_status] ? t(DOC_KEY[r.doc_status]) : r.doc_status} tone={docPill(r.doc_status)} value={r.doc_status} attrs={{ 'data-doc-status': r.doc_status }}
                      menu={!ro && recordMayHaveDoors(r) ? doors.menu(r.key, r.expense_no || r.supplier_no || r.filename) : undefined}
                      beside={r.pay_status === 'none' ? undefined : <span style={payPillOf(r.pay_status)} data-pay-status={r.pay_status}>{PAY_KEY[r.pay_status] ? t(PAY_KEY[r.pay_status]) : r.pay_status}</span>}>
                      {(r.paid || 0) > 0.005 && <FigureLine name="paid" label={t('aw_col_paid')} value={r.paid} />}
                      {(r.outstanding || 0) > 0.005 && <FigureLine name="outstanding" label={t('aw_col_outstanding')} value={r.outstanding} owed />}
                      <RecordChips r={r} onEntry={e => setEntry(e.id)} onRecord={k => rec.open(k)} />
                    </StatusCell>
                    <td style={{ ...td, whiteSpace: 'nowrap' }} onClick={e => e.stopPropagation()}>
                      <RowMenu items={() => [{ key: 'open', icon: 'ti-arrows-maximize', label: t('aw_er_open'), title: t('aw_er_open_title'), onPick: () => rec.open(r.key) }]} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
      {data && <Pagination page={page} total={data.total} limit={data.limit || limit} onPage={setPage} onLimit={n => { setLimit(n); setPage(1); }} />}
      <div style={note}>{t('aw_er_note')}</div>
      {creating && <BillDialog ctx={ctx} onClose={() => setCreating(false)} onSaved={() => { setCreating(false); ctx.bump(); }} />}
      {paying && <PaymentDialog ctx={ctx} bills={picked.map(id => ({ id }))} onClose={() => setPaying(false)} onDone={() => { setPaying(false); setPicked([]); ctx.bump(); }} />}
      {doors.dialog}
      {entry && <EntryDetailDialog entryId={entry} onClose={() => setEntry('')} />}
      {rec.key && <ExpenseRecordWindow ctx={ctx} recordKey={rec.key} onClose={rec.close} />}
    </div>
  );
}

/**
 * The pane. A company WITH an invoicing-portal account (a merchant row) opens on the register, with the
 * VAT line items one click away; a company without one has no records, so it opens on the VAT line items
 * when the operator has the Bookkeeping module, and on the "no portal account" card otherwise. A deep link
 * (?exp=) always opens on the register, under its window.
 */
export function ExpenseRecordsPane({ ctx }: { ctx: AwCtx }) {
  const { t } = useTranslation();
  const [params, setParams] = useSearchParams();
  const canVat = ctx.me.modules.includes('bookkeeping');
  if (ctx.summaryErr) return <div style={errBox}>{t('aw_summary_failed')}: {ctx.summaryErr}</div>;
  if (!ctx.summary) return <Loader />;
  if (!ctx.summary.hasMerchant) {
    if (canVat) {
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div style={{ ...card, padding: '9px 12px', fontSize: 12.5, color: 'var(--text-secondary)' }}><i className="ti ti-info-circle" aria-hidden /> {t('aw_er_no_portal_vat')}</div>
          <Bookkeeping embedded />
        </div>
      );
    }
    const canPortal = ctx.me.modules.includes('portal_accounts');
    return <Placeholder title={t('aw_sub_expense_records')} state="noportal" link={canPortal ? { href: '/m/portal_accounts', label: t('aw_open_portal_accounts') } : undefined} />;
  }
  const vat = canVat && params.get('exview') === 'vat' && !params.get('exp');
  const setView = (v: 'records' | 'vat') => setParams(p => { const n = new URLSearchParams(p); if (v === 'vat') n.set('exview', 'vat'); else n.delete('exview'); return n; }, { replace: true });
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {canVat && (
        <div role="group" aria-label={t('aw_er_view_label')} style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          <button type="button" aria-pressed={!vat} onClick={() => setView('records')} style={{ ...(!vat ? { ...chip, ...chipOn } : chip) }}>
            <i className="ti ti-receipt" aria-hidden /> {t('aw_er_view_records')}
          </button>
          <button type="button" aria-pressed={vat} onClick={() => setView('vat')} style={{ ...(vat ? { ...chip, ...chipOn } : chip) }}>
            <i className="ti ti-receipt-tax" aria-hidden /> {t('aw_er_view_vat')}
          </button>
        </div>
      )}
      {vat ? <Bookkeeping embedded /> : <RecordsRegister ctx={ctx} />}
    </div>
  );
}
